/**
 * `COMMAND_ACK.result`（`MAV_RESULT`）的分类与中文文案。
 *
 * ## 为什么全程只认枚举名，不认数字
 *
 * `MAV_RESULT` 的编号被抄错过：把 `IN_PROGRESS` 记成 1、`FAILED` 记成 5 ——
 * 那是把 `TEMPORARILY_REJECTED`（1）/`DENIED`（2）整体记错一位的结果。
 * 按数字写 `switch` 的代码在这种抄错下会**静默**地把"暂时拒绝"当成"正在执行"，
 * 于是永远等不到终态；反过来把 `IN_PROGRESS` 当终态，则会让"校准""返航"
 * 这类长任务一收到确认就被判失败 —— 两种错误在真机上都很致命。
 *
 * 所以这里的链路是：ACK 里的数字 → `@hgc/mavlink` 生成表里的枚举名 →
 * 本文件的处理方式与中文文案。生成产物（固定提交的上游 XML 快照）是唯一来源，
 * 本文件里没有任何一个手写的 `MAV_RESULT` 编号。
 *
 * 表里查不到的值一律归到 `unknown-result`，并把原值写进文案里，绝不猜。
 */

import { enumByName, enumEntryByValue, type EnumEntryDefinition } from '@hgc/mavlink';

/**
 * 一条命令的最终结论。
 *
 * 前 11 项来自 `MAV_RESULT`（名字与枚举一一对应，去掉前缀后小写）；
 * `in-progress` 是**唯一的双关项**：既表示"飞控说正在执行"，也表示"长任务在
 * 超过绝对上限后失去了联系"的终态 —— 后者**不是失败**，命令可能仍在飞机上跑，
 * 界面必须把它和"失败"区别呈现。
 * 最后 4 项不是协议结果，而是本地结论。
 */
export type CommandStatus =
  | 'accepted'
  | 'in-progress'
  | 'temporarily-rejected'
  | 'denied'
  | 'unsupported'
  | 'failed'
  | 'cancelled'
  | 'long-only'
  | 'int-only'
  | 'frame-unsupported'
  | 'not-in-control'
  | 'unknown-result'
  /** 本地：超过重传上限仍未收到任何确认（**不是**成功，也不是飞控的拒绝） */
  | 'no-ack'
  /** 本地：被门禁拦下，帧根本没有发出去 */
  | 'blocked'
  /** 本地：链路未打开或组帧失败，帧没有发出去 */
  | 'send-error'
  /** 本地：管理器已销毁，结果不再被跟踪（飞控可能已经执行了它） */
  | 'disposed';

/**
 * 收到这条结果之后该做什么。
 *
 * 与 `status` 分开是必要的：`in-progress` 与 `needs-long` 都有 `status`，
 * 但前者要继续等、后者要换帧类型重发 —— 用一个字段表达会逼出 if 套 if。
 */
export type ResultBehavior =
  /** 终态：成功 */
  | 'accepted'
  /** 不是终态：继续等后续 ACK（长任务） */
  | 'in-progress'
  /** 可以重发（有次数上限） */
  | 'retry'
  /** 终态：失败/拒绝 */
  | 'terminal'
  /** 换 `COMMAND_LONG` 重发一次 */
  | 'needs-long'
  /** 换 `COMMAND_INT` 重发一次 */
  | 'needs-int';

export interface MavResultInfo {
  readonly value: number;
  /** 枚举名；查不到时为 `null` */
  readonly name: string | null;
  readonly behavior: ResultBehavior;
  readonly status: CommandStatus;
  /** 面向飞手的中文结论。界面可以直接显示 */
  readonly text: string;
  readonly retryable: boolean;
}

/**
 * 每个结论的一句话中文标签。
 *
 * 用途是**和具体原因拼成一句人话**："执行失败：Mode change to LOITER failed: runup not complete"。
 * 通用的 {@link MavResultInfo.text} 是用来解释"这一类结果通常意味着什么"的，
 * 拼不进上面那句里（会变成一句半长的废话）。界面上的状态徽章也可以直接用这张表。
 */
export const COMMAND_STATUS_LABELS: Readonly<Record<CommandStatus, string>> = {
  accepted: '已接受',
  'in-progress': '仍在执行',
  'temporarily-rejected': '被暂时拒绝',
  denied: '被飞控拒绝',
  unsupported: '飞控不支持',
  failed: '执行失败',
  cancelled: '已取消',
  'long-only': '需要以 COMMAND_LONG 下发',
  'int-only': '需要以 COMMAND_INT 下发',
  'frame-unsupported': '坐标框架不受支持',
  'not-in-control': '控制源被拒绝',
  'unknown-result': '未识别的结果',
  'no-ack': '未收到确认',
  blocked: '已拦下',
  'send-error': '发送失败',
  disposed: '结果未知',
};

/**
 * 逐条处理方式与文案表，键是上游枚举名。
 *
 * 文案写给**飞手**，不是写给开发者：说清"发生了什么"以及"通常为什么"，
 * 而不是复述枚举名的英文。例如 `DENIED` 不能只说"被拒绝"——
 * ArduPilot 回 `DENIED` 的绝大多数场合是解锁前检查没通过。
 */
const HANDLING: Readonly<
  Record<
    string,
    { readonly behavior: ResultBehavior; readonly status: CommandStatus; readonly text: string }
  >
> = {
  MAV_RESULT_ACCEPTED: {
    behavior: 'accepted',
    status: 'accepted',
    text: '飞控已接受并执行',
  },
  MAV_RESULT_TEMPORARILY_REJECTED: {
    behavior: 'retry',
    status: 'temporarily-rejected',
    text: '飞控暂时拒绝（正在处理别的请求或前置条件还没就绪），可以重试',
  },
  MAV_RESULT_DENIED: {
    behavior: 'terminal',
    status: 'denied',
    text: '被飞控拒绝（通常是安全检查未通过，例如解锁前检查失败、当前模式下不允许）',
  },
  MAV_RESULT_UNSUPPORTED: {
    behavior: 'terminal',
    status: 'unsupported',
    text: '飞控不支持这条命令（当前固件没有实现，或这条命令不适用于该机型）',
  },
  MAV_RESULT_FAILED: {
    behavior: 'terminal',
    status: 'failed',
    text: '执行失败（飞控收到了命令但没能完成，常见原因是未解锁或前置条件不满足）',
  },
  MAV_RESULT_IN_PROGRESS: {
    behavior: 'in-progress',
    status: 'in-progress',
    text: '正在执行中（长任务，飞控会继续回报进度与最终结果）',
  },
  MAV_RESULT_CANCELLED: {
    behavior: 'terminal',
    status: 'cancelled',
    text: '命令已被取消',
  },
  MAV_RESULT_COMMAND_LONG_ONLY: {
    behavior: 'needs-long',
    status: 'long-only',
    text: '这条命令只能以 COMMAND_LONG（float 参数）下发',
  },
  MAV_RESULT_COMMAND_INT_ONLY: {
    behavior: 'needs-int',
    status: 'int-only',
    text: '这条命令只能以 COMMAND_INT（整数经纬度）下发',
  },
  MAV_RESULT_COMMAND_UNSUPPORTED_MAV_FRAME: {
    behavior: 'terminal',
    status: 'frame-unsupported',
    text: '飞控不支持命令里指定的坐标框架（MAV_FRAME），例如该机型没有地形高度',
  },
  MAV_RESULT_NOT_IN_CONTROL: {
    behavior: 'terminal',
    status: 'not-in-control',
    text: '飞控当前不接受这条指令的控制源（例如遥控器已接管、自动模式正在执行任务）',
  },
};

function infoFor(entry: EnumEntryDefinition): MavResultInfo {
  const handling = HANDLING[entry.name];
  if (handling === undefined) {
    return {
      value: entry.value,
      name: entry.name,
      behavior: 'terminal',
      status: 'unknown-result',
      // 上游新增了结果值而本文件还没跟上：明说"没收录"，并把枚举名给出来对照
      text: `飞控返回 ${entry.name}（本文件未收录这条结果的处理方式，按失败处理）`,
      retryable: false,
    };
  }
  return {
    value: entry.value,
    name: entry.name,
    behavior: handling.behavior,
    status: handling.status,
    text: handling.text,
    retryable: handling.behavior === 'retry',
  };
}

let table: Map<number, MavResultInfo> | null = null;

/** 数字 → 处理方式。懒建一次（枚举表本身也是懒建的，见 descriptor.ts）。 */
function resultTable(): Map<number, MavResultInfo> {
  if (table === null) {
    table = new Map();
    for (const entry of enumByName('MAV_RESULT')?.entries ?? []) {
      table.set(entry.value, infoFor(entry));
    }
  }
  return table;
}

/** 结果值的枚举名（`MAV_RESULT_DENIED`）；不在表里时为 `null` */
export function mavResultName(result: number): string | null {
  return enumEntryByValue('MAV_RESULT', result)?.name ?? null;
}

/**
 * 把一个 `COMMAND_ACK.result` 解释成"该怎么处理 + 该怎么说"。
 *
 * 传入未识别的值**不抛错**：链路另一端可能是比本仓库更新或更旧的固件，
 * 一帧看不懂的结果值不该让整条命令流水线停摆。
 */
export function mavResultInfo(result: number): MavResultInfo {
  const known = resultTable().get(result);
  if (known !== undefined) return known;
  return {
    value: result,
    name: mavResultName(result),
    behavior: 'terminal',
    status: 'unknown-result',
    text: `飞控返回了未识别的结果值 ${result}（本仓库的协议表里没有这一项，按失败处理）`,
    retryable: false,
  };
}

/** 面向飞手的中文文案。 */
export function mavResultText(result: number): string {
  return mavResultInfo(result).text;
}

/**
 * 全部 `MAV_RESULT` 的文案表（按值升序）。
 *
 * 给界面的"结果说明"用：用户看到一条 `MAV_RESULT_CANCELLED` 时应该能查到
 * "这是什么意思"，而不是只能看着英文枚举名猜。
 */
export function allMavResultTexts(): readonly MavResultInfo[] {
  return [...resultTable().values()].sort((a, b) => a.value - b.value);
}
